import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, Stack, Typography } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { apiErrorMessage } from '../api';
import { launchColors } from '../launchTheme';
import { alcoholApi } from './alcoholApi';
import { AlcoholImportPreview } from './alcoholTypes';

interface Props {
  open: boolean;
  onClose: () => void;
  onImported: (message: string) => void;
}

const Line: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box>
    <Typography sx={{ fontSize: 13, fontWeight: 700, color: launchColors.navy }}>{label}</Typography>
    <Typography component="div" sx={{ fontSize: 13 }}>{children}</Typography>
  </Box>
);

const list = (items: string[]) => items.join(', ') || 'Inga';

const AlcoholImportDialog: React.FC<Props> = ({ open, onClose, onImported }) => {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<AlcoholImportPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setPreview(null);
    setError(null);
  }, [open]);

  const choose = async (next: File | null) => {
    setFile(next);
    setPreview(null);
    setError(null);
    if (!next) return;
    setBusy(true);
    try {
      setPreview(await alcoholApi.previewImport(next));
    } catch (requestError) {
      setError(apiErrorMessage(requestError, 'Filen kunde inte läsas.'));
    } finally {
      setBusy(false);
    }
  };

  const mode: 'all' | 'missing' = preview && preview.existing > 0 ? 'missing' : 'all';
  const toAdd = preview ? preview.total - preview.alreadyThere : 0;

  const run = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const result = await alcoholApi.runImport(file, mode);
      onImported([
        `${result.imported} produkter lästes in.`,
        result.skipped ? `${result.skipped} fanns redan.` : '',
        result.producers ? `${result.producers} nya producenter.` : '',
      ].filter(Boolean).join(' '));
    } catch (requestError) {
      setError(apiErrorMessage(requestError, 'Importen misslyckades.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Läs in Excel</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5}>
          <Typography sx={{ fontSize: 13 }}>
            Välj filen med bladet PRODUKTER ALKOHOL. Produkter, producenttexter och anteckningar läses in. Inget sparas förrän du väljer Läs in.
          </Typography>
          <Box>
            <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} disabled={busy}>
              Välj fil
              <input
                hidden
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                aria-label="Excel-fil"
                onChange={(event) => { void choose(event.target.files?.[0] ?? null); event.target.value = ''; }}
              />
            </Button>
            {file && <Typography component="span" sx={{ ml: 1, fontSize: 13 }}>{file.name}</Typography>}
          </Box>
          {busy && <LinearProgress />}
          {error && <Alert severity="error">{error}</Alert>}
          {preview && (
            <Stack spacing={1.25}>
              {preview.existing > 0 && (
                <Alert severity="info">
                  Det finns redan {preview.existing} produkter. Bara de som saknas läggs till ({toAdd} st). Befintliga produkter och vingårdstexter ändras inte.
                </Alert>
              )}
              <Line label={`${preview.total} produkter i filen`}>
                Godkända {preview.approved} · Kommande {preview.coming} · Med anteckning {preview.withNote}
              </Line>
              <Line label={`${preview.producers} producenter`}>
                {preview.producersAlreadyThere ? `${preview.producersAlreadyThere} finns redan. ` : ''}
                Utan vingårdstext: {list(preview.producersWithoutText)}
              </Line>
              {preview.textsWithoutProducts.length > 0 && (
                <Line label="Producenttexter utan produkter">{list(preview.textsWithoutProducts)}</Line>
              )}
              <Line label={`${preview.countries} landstexter läses inte in`}>
                {preview.countriesWithoutText.length ? `Utan text: ${list(preview.countriesWithoutText)}` : 'Alla har text.'}
              </Line>
              <Line label={`Dubbletter (${preview.duplicates.length})`}>
                {preview.duplicates.map((item) => `${item.articleNumber} (${item.count})`).join(', ') || 'Inga'}
              </Line>
              <Line label={`Namn över 35 tecken (${preview.longNames.length})`}>{list(preview.longNames)}</Line>
              {preview.unparsed.length > 0 && <Line label={`Värden som inte kunde tolkas (${preview.unparsed.length})`}>{list(preview.unparsed)}</Line>}
              {preview.unknownColumns.length > 0 && <Line label="Kolumner utan eget fält (sparas under Övrigt)">{list(preview.unknownColumns)}</Line>}
              {preview.skippedRows.length > 0 && <Line label={`Rader som hoppas över (${preview.skippedRows.length})`}>{preview.skippedRows.join(' · ')}</Line>}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Avbryt</Button>
        <Button variant="contained" disabled={busy || !preview || toAdd === 0} onClick={run}>
          {preview ? `Läs in ${toAdd} produkter` : 'Läs in'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AlcoholImportDialog;
